import { ConfirmDialog } from '@amelio/ui-library';
import { useState } from 'react';

import { AddCompetenciesModal } from './AddCompetenciesModal';
import { AddedCompetenciesSection } from './AddedCompetenciesSection';
import { CompetencyGroupAccordion } from './CompetencyGroupAccordion';
import { DeactivatedProfileBanner } from './DeactivatedProfileBanner';
import { useProfileSelectorState } from './hooks/useProfileSelectorState';
import { ProfileCard } from './ProfileCard';
import { ProfileGrid } from './ProfileGrid';
import { ProfileSelectorFooter } from './ProfileSelectorFooter';
import { ProfileSelectorHeader } from './ProfileSelectorHeader';
import OverlayLoader from '../OverlayLoader/OverlayLoader';

import type { CompetencyOverlay } from './hooks/useProfileSelectorState';
import type { RosterEmployee } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import type { ReactNode } from 'react';

import { isValidProfileId } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import { useGetUserById } from '@/api/directory';
import { useAmelioTranslations } from '@/hooks';
import { fullName } from '@/pages/CompetencyProfilesAdmin/helpers';
import { getLocalizedText } from '@/utils/localization';

/**
 * Whose profile is chosen: the one the employee holds, or the one of the position they aim for.
 * Both can be tailored to the employee, each with its own overlay.
 */
export type ProfileFor = 'current' | 'target';

/** The glass the selector lays its content on, for a caller's own fields to sit on the same one. */
export const PROFILE_SELECTOR_SURFACE = 'ds-rounded-md border border-white bg-white/45';

export type ProfileSelectorResult = {
    profileId: string;
    addedCompetencyIds: string[];
    removedCompetencyIds: string[];
    /** The employee's levels chosen here, by competency; only while the levels are offered. */
    competencyProficiencyLevels?: Record<string, string>;
};

type ProfileSelectorProps = {
    employee: RosterEmployee;
    initialProfileId?: string;
    isSubmitting?: boolean;
    onConfirm: (result: ProfileSelectorResult) => void;
    /** Absent, no profile can be created from the selector. */
    onCreateGuided?: () => void;
    onCancel: () => void;
    /** When false, the "Create new profile" tile is disabled with an explanatory tooltip. */
    canManageCompetencies?: boolean;
    profileFor?: ProfileFor;
    /** The adjustments the selection starts from; the employee's own current overlay by default. */
    overlay?: CompetencyOverlay;
    /** The competencies the employee already holds in their current profile, shown greyed. */
    heldCompetencyIds?: ReadonlySet<string>;
    /** Shown under the header's subtitle, such as the target position's own fields. */
    children?: ReactNode;
};

export const ProfileSelector = ({
    employee,
    initialProfileId,
    isSubmitting = false,
    onConfirm,
    onCreateGuided,
    onCancel,
    canManageCompetencies = false,
    profileFor = 'current',
    overlay = employee,
    heldCompetencyIds,
    children,
}: ProfileSelectorProps) => {
    const { t } = useAmelioTranslations();
    const { data: reader } = useGetUserById({ userId: 'me' });
    const isForTarget = profileFor === 'target';
    // A level is the employee's own: only the profile they hold sets one, never a target's. And it
    // is set by someone else, once the reader is known: the server refuses a level on oneself.
    const offersLevels = !isForTarget && reader !== undefined && reader.id !== employee.id;

    const {
        search,
        setSearch,
        removedIds,
        addedIds,
        setAddedIds,
        showAddModal,
        setShowAddModal,
        expandedGroups,
        initialProfile,
        selectedProfile,
        profileByGroup,
        profileCompetencyIds,
        addedRows,
        addedByGroup,
        filteredProfiles,
        selectedProfileCounts,
        isLoading,
        getProfileCounts,
        selectProfile,
        toggleRemoved,
        toggleGroupExpanded,
        removeAddedCompetency,
        employeeLevels,
        changedLevels,
    } = useProfileSelectorState({
        overlay,
        initialProfileId,
        levelsEmployeeId: offersLevels ? employee.id : undefined,
    });

    const employeeName = fullName(employee);
    const isEditing = isValidProfileId(initialProfileId);
    const [showChangeConfirm, setShowChangeConfirm] = useState(false);

    const removedProfileCompetencyIds = Array.from(removedIds).filter((id) =>
        profileCompetencyIds.includes(id),
    );
    const isAdjusted = addedIds.length > 0 || removedProfileCompetencyIds.length > 0;

    const buildResult = (): ProfileSelectorResult => {
        const result = {
            profileId: selectedProfile!.id,
            addedCompetencyIds: addedIds,
            removedCompetencyIds: removedProfileCompetencyIds,
        };

        if (!employeeLevels) return result;

        // A level picked before the competency was removed must not be saved for it.
        const competencyProficiencyLevels = Object.fromEntries(
            Object.entries(changedLevels).filter(([competencyId]) => !removedIds.has(competencyId)),
        );
        return { ...result, competencyProficiencyLevels };
    };

    // Replacing the profile drops the employee's overlay on it: worth a confirmation.
    const isProfileChange =
        isValidProfileId(initialProfileId) &&
        selectedProfile !== undefined &&
        selectedProfile.id !== initialProfileId;

    const handleConfirm = () => {
        if (!selectedProfile) return;
        onConfirm(buildResult());
    };

    const handleSaveClick = () => {
        if (!selectedProfile) return;
        if (isProfileChange) {
            setShowChangeConfirm(true);
        } else {
            handleConfirm();
        }
    };

    return (
        <>
            <ProfileSelectorHeader
                employeeName={employeeName}
                profileFor={profileFor}
                isEditing={isEditing}
                isSubmitting={isSubmitting}
                showSearch={!selectedProfile}
                search={search}
                onSearchChange={setSearch}
                onCancel={onCancel}
            >
                {children}
            </ProfileSelectorHeader>

            <div className={`${PROFILE_SELECTOR_SURFACE} p-6 sm:p-8`}>
                {isLoading ? (
                    <div className={'flex h-[320px] items-center justify-center'}>
                        <OverlayLoader isLoading={true} />
                    </div>
                ) : selectedProfile ? (
                    <div className={'flex flex-col gap-7'}>
                        <ProfileCard
                            profile={selectedProfile}
                            counts={selectedProfileCounts}
                            adjustedFor={isForTarget && isAdjusted ? employee.firstName : undefined}
                            onSelectAnother={() => selectProfile('')}
                        />

                        {!isForTarget && !selectedProfile.isActive && (
                            <DeactivatedProfileBanner
                                profile={selectedProfile}
                                employeeId={employee.id}
                                onDone={onCancel}
                            />
                        )}

                        <div className={'mt-4 flex flex-col gap-4 border-t border-gray-200 pt-8'}>
                            <p className={'text-body-lg text-navy font-semibold'}>
                                {t(
                                    'competencies.competencyProfilesAdmin.selector.competenciesTitle',
                                )}
                            </p>

                            {profileByGroup.map(({ group, items }) => (
                                <CompetencyGroupAccordion
                                    key={group.id}
                                    group={group}
                                    items={items}
                                    isExpanded={expandedGroups.has(group.id)}
                                    removedIds={removedIds}
                                    heldIds={heldCompetencyIds}
                                    onToggleExpanded={() => toggleGroupExpanded(group.id)}
                                    onToggleRemoved={toggleRemoved}
                                    employeeLevels={employeeLevels}
                                />
                            ))}

                            <AddedCompetenciesSection
                                addedByGroup={addedByGroup}
                                totalCount={addedRows.length}
                                heldIds={heldCompetencyIds}
                                onRemoveCompetency={removeAddedCompetency}
                            />
                        </div>
                    </div>
                ) : (
                    <ProfileGrid
                        profiles={filteredProfiles}
                        onSelectProfile={selectProfile}
                        onCreateGuided={onCreateGuided}
                        canManageCompetencies={canManageCompetencies}
                        getProfileCounts={getProfileCounts}
                    />
                )}

                <ProfileSelectorFooter
                    isEditing={isEditing || isForTarget}
                    isSubmitting={isSubmitting}
                    hasSelectedProfile={Boolean(selectedProfile)}
                    onCancel={onCancel}
                    onAddCompetencies={() => setShowAddModal(true)}
                    onConfirm={handleSaveClick}
                />
            </div>

            {showAddModal && (
                <AddCompetenciesModal
                    lockedCompetencyIds={profileCompetencyIds}
                    heldCompetencyIds={heldCompetencyIds}
                    selectedCompetencyIds={addedIds}
                    onConfirm={(ids) => {
                        setAddedIds(ids);
                        setShowAddModal(false);
                    }}
                    onCancel={() => setShowAddModal(false)}
                />
            )}

            {showChangeConfirm && initialProfile && selectedProfile && (
                <ConfirmDialog
                    isOpen={showChangeConfirm}
                    title={t('competencies.competencyProfilesAdmin.selector.changeConfirmTitle')}
                    message={t(
                        isForTarget
                            ? 'competencies.competencyProfilesAdmin.selector.changeTargetConfirmBody'
                            : 'competencies.competencyProfilesAdmin.selector.changeConfirmBody',
                        {
                            name: employeeName,
                            current: getLocalizedText(initialProfile.name),
                            next: getLocalizedText(selectedProfile.name),
                        },
                    )}
                    confirmLabel={t(
                        'competencies.competencyProfilesAdmin.selector.changeConfirmConfirm',
                    )}
                    cancelLabel={t(
                        'competencies.competencyProfilesAdmin.selector.changeConfirmCancel',
                    )}
                    onConfirm={() => {
                        setShowChangeConfirm(false);
                        handleConfirm();
                    }}
                    onCancel={() => setShowChangeConfirm(false)}
                    isLoading={isSubmitting}
                    dataTestId={'roster-selector-change-confirm'}
                    zIndex={60}
                />
            )}
        </>
    );
};
